<template>
  <div>
    <menu-content-container :padding-left-right="8" :margin-top="16">
      <content-row>
        <TInput
          v-model="alt"
          :label="$t('forms.image_alt')"
          data-testid="loop-image-alt"
        ></TInput>
      </content-row>
      <content-row>
        <TInput
          v-model="title"
          :label="$t('forms.image_title')"
          data-testid="loop-image-title"
        ></TInput>
      </content-row>
      <content-row>
        <TInput
          v-model="href"
          :label="$t('forms.image_link')"
          data-testid="loop-image-link"
        ></TInput>
      </content-row>
    </menu-content-container>

    <menu-content-container
      :header="$t('product.img_size')"
      :padding-left-right="8"
    >
      <content-row :margin-top="16">
        <t-slider
          v-model="width"
          xs
          :min="30"
          :max="containerWidth"
          :unit="'px'"
          data-testid="loop-image-width"
        ></t-slider>
      </content-row>
    </menu-content-container>

    <menu-content-container :padding-left-right="8">
      <TLabel
        :changed="isChanged('align')"
        :tooltip="$t('forms.reset_value')"
        @reset="resetChildAttr('align')"
        >{{ $t("forms.image_align") }}</TLabel
      >
      <content-row justify-between :margin-top="4">
        <TButtonGroup>
          <TGroupButton
            data="left"
            data-testid="align-left"
            small
            :active="align === 'left'"
            @button-clicked="setAlign('left')"
          >
            <AligmentLeft />
          </TGroupButton>
          <TGroupButton
            data="center"
            data-testid="align-center"
            small
            :active="align === 'center'"
            @button-clicked="setAlign('center')"
          >
            <AligmentCenter />
          </TGroupButton>
          <TGroupButton
            data="right"
            data-testid="align-right"
            small
            :active="align === 'right'"
            @button-clicked="setAlign('right')"
          >
            <AligmentRight />
          </TGroupButton>
        </TButtonGroup>

        <t-switch
          v-model="fluidOnMobile"
          :label="$t('forms.fluid_on_mobile')"
          :changed="isChanged('fluid-on-mobile')"
          :reset-tooltip="$t('forms.reset_value')"
          @reset="resetChildAttr('fluid-on-mobile')"
        ></t-switch>
      </content-row>
    </menu-content-container>

    <borderRadiusForm
      data-testid="loop-image-border-radius"
      :label="$t('forms.border_radius')"
      :padding-left-right="8"
      :min-y="0"
      :max-y="containerWidth"
      :min-x="0"
      :max-x="containerWidth"
      external-usage
      :external-border-radius="borderRadius"
      :changed="isChanged('border-radius')"
      :default-value="getChildDefault('border-radius')"
      @borderRadiusChanged="onBorderRadiusChanged"
      @reset="resetChildAttr('border-radius')"
    ></borderRadiusForm>

    <paddingForm
      external-usage
      :external-padding="padding"
      :padding-left-right="8"
      :border-bottom="false"
      class="!mb-2"
      data-testid="loop-image-padding"
      :changed="isChanged('padding')"
      :default-value="getChildDefault('padding')"
      @paddingChanged="onPaddingChanged"
      @reset="resetChildAttr('padding')"
    ></paddingForm>
  </div>
</template>

<script lang="ts" setup>
  import paddingForm from "../paddingForm.vue";
  import borderRadiusForm from "../borderRadiusForm.vue";

  import {
    TButtonGroup,
    TGroupButton,
    TSliderClassic as TSlider,
    TSwitch,
    TInput,
    TLabel,
  } from "@topol/twig-ui";

  import { AligmentLeft, AligmentCenter, AligmentRight } from "@topol/icons";

  import { TopolLoopImage } from "@/schemas/template/blocks/loopBlockSchema";

  import { useMjmlStore } from "@/stores/mjmlStore";
  import { normalizeSpacing } from "@/utils/spacingUtils";
  import { computed } from "vue";

  const props = defineProps<{
    colIndex: number;
    blockIndex: number;
    block: TopolLoopImage;
  }>();

  const mjmlStore = useMjmlStore();

  // --- Reset helpers ---
  function getChildDefault(prop: string) {
    const baseValues = mjmlStore.getBaseAttrWithDefault("mj-image", prop);
    return baseValues?.value ?? baseValues?.defaultValue;
  }

  function isChanged(prop: string) {
    const currentValue =
      props.block.attributes[prop as keyof typeof props.block.attributes];
    const defaultValue = getChildDefault(prop);
    if (currentValue === undefined || defaultValue === undefined) return false;
    if (typeof currentValue === "string" && typeof defaultValue === "string") {
      const normCurrent = normalizeSpacing(currentValue);
      const normDefault = normalizeSpacing(defaultValue);
      if (normCurrent && normDefault) return normCurrent !== normDefault;
    }
    return String(currentValue) !== String(defaultValue);
  }

  function resetChildAttr(prop: string) {
    const defaultValue = getChildDefault(prop);
    mjmlStore.updateLoopBlockChildAttribute({
      columnIndex: props.colIndex,
      blockIndex: props.blockIndex,
      prop,
      value: defaultValue,
    });
  }

  const block = computed(() => {
    return props.block;
  });

  const alt = computed({
    get() {
      return block.value.attributes.alt || "";
    },
    set(newValue: string) {
      mjmlStore.updateLoopBlockChildAttribute({
        columnIndex: props.colIndex,
        blockIndex: props.blockIndex,
        prop: "alt",
        value: newValue,
      });
    },
  });

  const title = computed({
    get() {
      return block.value.attributes.title || "";
    },
    set(newValue: string) {
      mjmlStore.updateLoopBlockChildAttribute({
        columnIndex: props.colIndex,
        blockIndex: props.blockIndex,
        prop: "title",
        value: newValue,
      });
    },
  });

  const href = computed({
    get() {
      return block.value.attributes.href || "";
    },
    set(newValue: string) {
      mjmlStore.updateLoopBlockChildAttribute({
        columnIndex: props.colIndex,
        blockIndex: props.blockIndex,
        prop: "href",
        value: newValue,
      });
    },
  });

  const containerWidth = computed(() => {
    return block.value.attributes.containerWidth || 600;
  });

  const fluidOnMobile = computed({
    get() {
      return block.value.attributes["fluid-on-mobile"] === "true";
    },

    set(value: boolean) {
      mjmlStore.updateLoopBlockChildAttribute({
        columnIndex: props.colIndex,
        blockIndex: props.blockIndex,
        prop: "fluid-on-mobile",
        value: value ? "true" : "false",
      });
    },
  });

  const align = computed({
    get() {
      return block.value.attributes["align"] || "center";
    },
    set(val: string) {
      mjmlStore.updateLoopBlockChildAttribute({
        columnIndex: props.colIndex,
        blockIndex: props.blockIndex,
        prop: "align",
        value: val,
      });
    },
  });

  const width = computed({
    get() {
      if (block.value.attributes["width"]) {
        return parseInt(block.value.attributes["width"].toString());
      }
      return containerWidth.value;
    },
    set(val: number) {
      mjmlStore.updateLoopBlockChildAttribute({
        columnIndex: props.colIndex,
        blockIndex: props.blockIndex,
        prop: "width",
        value: val,
      });
    },
  });

  const padding = computed({
    get() {
      return block.value.attributes["padding"] || "0px";
    },
    set(val: string) {
      mjmlStore.updateLoopBlockChildAttribute({
        columnIndex: props.colIndex,
        blockIndex: props.blockIndex,
        prop: "padding",
        value: val,
      });
    },
  });

  const borderRadius = computed({
    get() {
      return block.value.attributes["border-radius"];
    },
    set(val: string) {
      mjmlStore.updateLoopBlockChildAttribute({
        columnIndex: props.colIndex,
        blockIndex: props.blockIndex,
        prop: "border-radius",
        value: val,
      });
    },
  });

  function setAlign(direction: string) {
    align.value = direction;
  }

  function onPaddingChanged(val: string) {
    padding.value = val;
  }

  function onBorderRadiusChanged(val: string) {
    borderRadius.value = val;
  }
</script>
